"use client";

import {
  createContext,
  type Dispatch,
  type ReactNode,
  type RefObject,
} from "react";
import type { FormFieldSchema, SlidesRef } from "@huub-web/ui";
import type {
  MaximumMortgageWizardState,
  MortgageWizardAction,
  MaximumMortgageWizardFormValues,
  MaximumMortgageWizardSlide,
  MaximumMortgageFlowConfig,
} from "../types/MaximumMortgageWizard";
import type { BankSelectOption } from "../hooks/useBanks";

export type MaximumMortgageWizardContextType = {
  /**
   * Current reducer state containing the active slide,
   * collected form values, and per-slide validation status.
   */
  state: MaximumMortgageWizardState;
  /**
   * Raw dispatch for the wizard reducer. Prefer the helper
   * methods (`updateFormValues`, `setSlideValidation`) over
   * dispatching actions directly.
   */
  dispatch: Dispatch<MortgageWizardAction>;
  /**
   * Merges partial form values into the wizard state.
   */
  updateFormValues: (values: Partial<MaximumMortgageWizardFormValues>) => void;
  /**
   * Marks a slide as valid or invalid so navigation guards
   * can prevent advancing past incomplete slides.
   */
  setSlideValidation: (
    slide: MaximumMortgageWizardSlide,
    isValid: boolean,
  ) => void;
  /**
   * Ordered list of slides the user will step through.
   * Determined dynamically based on form values (e.g. purpose).
   */
  slideOrder: MaximumMortgageWizardSlide[];
  /**
   * Zero-based index of the current slide within `slideOrder`.
   */
  currentSlideIndex: number;
  /**
   * Whether the user is allowed to advance to the next slide
   * (current slide must be valid and not the last one).
   */
  canNavigateNext: boolean;
  /**
   * Whether the user is allowed to go back to the previous slide
   * (must not be on the first slide).
   */
  canNavigatePrevious: boolean;
  /**
   * Map of slide identifiers to their rendered content.
   * Each entry can be a plain `ReactNode`, an object with an
   * optional URL `slug` and a `component`, or `null` to skip.
   */
  slides: Partial<
    Record<
      MaximumMortgageWizardSlide,
      { slug?: string; component: ReactNode } | ReactNode | null
    >
  >;
  /**
   * Ref to the underlying `<Slides>` component, used for
   * imperative slide transitions (e.g. `goTo`, `next`).
   */
  slidesRef: RefObject<SlidesRef | null>;
  /**
   * Validates the current slide form and, if valid, advances
   * to the next slide.
   */
  handleNext: () => void;
  /**
   * Navigates back to the previous slide without validation.
   */
  handlePrevious: () => void;
  /**
   * Validates and submits the entire wizard (final slide action).
   */
  handleSubmit: () => void;
  /**
   * Programmatically advances to the next slide, bypassing
   * form validation. Use with caution.
   */
  navigateToNext: () => void;
  /**
   * Registers a slide's `<form>` ref so the wizard can
   * trigger its native validation and submit externally.
   */
  registerForm: (
    slide: MaximumMortgageWizardSlide,
    formRef: RefObject<HTMLFormElement | null>,
  ) => void;
  /**
   * Triggers `requestSubmit()` on the currently active
   * slide's registered form ref.
   */
  submitCurrentForm: () => void;
  /**
   * Whether the wizard is performing an async operation
   * (e.g. submitting to the API).
   */
  loading: boolean;
  /**
   * Backend-driven flow configuration. When present, restricts
   * which mortgage types are available in the wizard.
   */
  flowConfig?: MaximumMortgageFlowConfig;
  /**
   * Resolved bank options ready for use in a Select component.
   * Populated once by the provider from the `getBanks` prop; shared
   * across all slides to avoid duplicate network requests.
   */
  bankOptions: BankSelectOption[];
  /**
   * Whether the bank list is currently being fetched.
   */
  banksLoading: boolean;
  /**
   * Called when the enriched field schema changes — on field registration,
   * deregistration, and value changes. Provides the full schema with
   * fieldKey, label, type, step, and current value for AI agent awareness.
   */
  onFieldSchemaChange?: (
    slideKey: MaximumMortgageWizardSlide,
    schema: FormFieldSchema[],
  ) => void;
};

/**
 * Context for the Maximum Mortgage Wizard component.
 */
export const MaximumMortgageWizardContext =
  createContext<MaximumMortgageWizardContextType | null>(null);
